import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Box,
  Typography,
  makeStyles,
  CircularProgress,
  Snackbar,
  IconButton,
  Divider,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    transition: 'border .3s ease-in-out, background-color .3s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dragActive: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.action.hover,
  },
  fileIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  uploadIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  fileInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  fileDetailsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  fileIconSmall: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  hidden: {
    display: 'none',
  },
  currentFileName: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
}));

const FileOverwriteDialog = ({ isOpen, onClose, onOverwrite, existingFileName = '' }) => {
  const classes = useStyles();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files[0]);
    }
  };

  const isExcelFile = (file) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
    ];
    const validExtensions = ['.xlsx', '.xls', '.xlsm'];

    if (validTypes.includes(file.type)) return true;

    const fileName = file.name.toLowerCase();
    return validExtensions.some((ext) => fileName.endsWith(ext));
  };

  const handleFiles = (file) => {
    if (isExcelFile(file)) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please upload only Excel files (.xlsx, .xls, or .xlsm)');
      setSelectedFile(null);
    }
  };

  const handleOverwrite = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      await onOverwrite(selectedFile);
      resetState();
      onClose();
    } catch (err) {
      console.error('Overwrite failed:', err);
      setUploading(false);
    }
  };

  const resetState = () => {
    setSelectedFile(null);
    setUploading(false);
    setError(null);
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={uploading ? null : handleCancel}
        fullWidth
        maxWidth="sm"
        aria-labelledby="file-overwrite-dialog-title"
      >
        <DialogTitle id="file-overwrite-dialog-title">
          <Box display="flex" justifyContent="space-between" alignContent="center">
            <Typography variant="h5">Overwrite File</Typography>
            <IconButton onClick={handleCancel} size="small" disabled={uploading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText>
            Replacing: <span className={classes.currentFileName}>{existingFileName}</span>
            <br />
            Drag and drop an Excel file (.xlsx, .xls, or .xlsm) here, or click to select a new file.
            The existing file will be overwritten.
          </DialogContentText>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            className={classes.hidden}
            accept=".xlsx,.xls,.xlsm,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
          />

          <Paper
            className={`${classes.dropzone} ${dragActive ? classes.dragActive : ''}`}
            onClick={() => fileInputRef.current.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            elevation={0}
          >
            {selectedFile ? (
              <>
                <InsertDriveFileIcon className={classes.fileIcon} />
                <Typography variant="h6">File Selected</Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="h6">Drag & Drop File Here</Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse
                </Typography>
              </>
            )}
          </Paper>

          {selectedFile && (
            <Box className={classes.fileInfo}>
              <Box className={classes.fileDetailsContainer}>
                <InsertDriveFileIcon className={classes.fileIconSmall} />
                <Typography variant="subtitle1" noWrap>
                  {selectedFile.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {formatFileSize(selectedFile.size)} | {selectedFile.type || 'Unknown type'}
              </Typography>
            </Box>
          )}

          {uploading && (
            <Box mt={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={24} />
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                Overwriting...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary" disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleOverwrite}
            color="primary"
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={<CloudUploadIcon />}
          >
            Overwrite
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileOverwriteDialog;
